import React from 'react';
import {
    Target,
    FileText,
    Rocket,
    MessageSquare,
    CheckCircle,
    AlertTriangle,
    Info,
    AlertCircle
} from 'lucide-react';

/**
 * UsageLimitsCard - Displays current feature usage and limits
 * Shows progress bars with color coding based on remaining usage
 * ONLY displays data from API - no hardcoded fallbacks
 */
const UsageLimitsCard = ({ usageStats, tier, onUpgrade, loading, error }) => {
    // Check if user is professional or enterprise tier
    const isProfessional = ['professional', 'ultimate', 'enterprise'].includes(tier);

    // Feature display configuration (labels and icons only, no limits)
    const featureConfig = {
        roadmaps: {
            label: 'Roadmaps',
            icon: Target,
            professionalOnly: false
        },
        resume_improvements: {
            label: 'Resume Reports',
            icon: FileText,
            professionalOnly: false
        },
        portfolio_projects: {
            label: 'Portfolio Ideas',
            icon: Rocket,
            professionalOnly: true  // Only show for professional+
        },
        interview_questions: {
            label: 'Interview Sets',
            icon: MessageSquare,
            professionalOnly: false
        },
        interview_evaluations: {
            label: 'Answer Reviews',
            icon: CheckCircle,
            professionalOnly: false
        }
    };

    // Calculate progress percentage and color
    const getProgressInfo = (used, limit) => {
        if (limit === null || limit === undefined) {
            return { percentage: 0, color: 'bg-emerald-500', textColor: 'text-emerald-400', status: 'unlimited' };
        }

        const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

        if (percentage >= 100) {
            return { percentage: 100, color: 'bg-red-500', textColor: 'text-red-400', status: 'exhausted' };
        } else if (percentage >= 80) {
            return { percentage, color: 'bg-yellow-500', textColor: 'text-yellow-400', status: 'low' };
        } else {
            return { percentage, color: 'bg-emerald-500', textColor: 'text-emerald-400', status: 'good' };
        }
    };

    // Show loading state
    if (loading) {
        return (
            <div className="mt-4 bg-[#0B0F19] border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-center py-4">
                    <div className="animate-spin w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full" />
                    <span className="ml-2 text-xs text-slate-400">Loading usage...</span>
                </div>
            </div>
        );
    }

    // Show error state if data failed to load
    if (error) {
        return (
            <div className="mt-4 bg-[#0B0F19] border border-red-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs">Failed to load usage data</span>
                </div>
            </div>
        );
    }

    // No data available
    if (!usageStats || Object.keys(usageStats).length === 0) {
        return (
            <div className="mt-4 bg-[#0B0F19] border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400">
                    <Info className="w-4 h-4" />
                    <span className="text-xs">No usage data available</span>
                </div>
            </div>
        );
    }

    // Filter features: only show those in featureConfig AND filter out professional-only features for non-pro users
    const displayFeatures = Object.entries(usageStats).filter(
        ([key]) => {
            const config = featureConfig[key];
            if (!config) return false;
            // Hide professional-only features from non-professional users
            if (config.professionalOnly && !isProfessional) return false;
            return true;
        }
    );

    if (displayFeatures.length === 0) {
        return null;
    }

    return (
        <div className="mt-4 bg-[#0B0F19] border border-white/10 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-xs font-mono text-slate-400 uppercase tracking-wider">
                    Monthly Usage
                </h3>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${tier === 'free'
                    ? 'bg-yellow-500/20 text-yellow-400'
                    : 'bg-emerald-500/20 text-emerald-400'
                    }`}>
                    {tier?.toUpperCase() || 'FREE'}
                </span>
            </div>

            <div className="space-y-3">
                {displayFeatures.map(([featureKey, data]) => {
                    const config = featureConfig[featureKey];
                    const Icon = config.icon;
                    const { used, limit, unlimited } = data;
                    const progressInfo = getProgressInfo(used, limit);

                    return (
                        <div key={featureKey} className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon className="w-3 h-3 text-slate-500" />
                                    <span className="text-xs text-slate-300">{config.label}</span>
                                </div>
                                <span className={`text-xs font-mono ${progressInfo.textColor}`}>
                                    {unlimited ? (
                                        <span className="text-emerald-400">∞</span>
                                    ) : (
                                        `${used}/${limit}`
                                    )}
                                </span>
                            </div>

                            {!unlimited && (
                                <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${progressInfo.color} transition-all duration-500`}
                                        style={{ width: `${progressInfo.percentage}%` }}
                                    />
                                </div>
                            )}

                            {progressInfo.status === 'exhausted' && (
                                <div className="flex items-center gap-1 text-[10px] text-red-400">
                                    <AlertTriangle className="w-2.5 h-2.5" />
                                    <span>Limit reached</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Low usage warning or upgrade prompt */}
            {tier === 'free' || displayFeatures.some(([, data]) => {
                const { used, limit } = data;
                return limit && used >= limit * 0.8;
            }) ? (
                <button
                    onClick={onUpgrade}
                    className="w-full py-2 text-xs bg-gradient-to-r from-emerald-500/10 to-teal-500/10 hover:from-emerald-500/20 hover:to-teal-500/20 text-emerald-400 border border-emerald-500/20 rounded-lg transition-colors font-mono uppercase flex items-center justify-center gap-2"
                >
                    <Info className="w-3 h-3" />
                    {tier === 'free' ? 'Unlock More' : 'Upgrade Plan'}
                </button>
            ) : null}
        </div>
    );
};

export default UsageLimitsCard;

